import { useEffect, useMemo, useState } from "react";
import ProductSKUSearch from "../common/ProductSKUSearch";
import LocationSelect from "../common/LocationSelect";
import inventoryAPI from "../../../../api/inventory/inventoryApi";
import {
  ADJUSTMENT_TYPES,
  getReasonsForType,
} from "../../../../constants/inventoryConstants";
import { showError, showSuccess } from "../../../../utils/toastUtils";

export default function QuickAddTab({
  initialItem,
  defaultLocationId,
  onSuccess,
}) {
  
  const INITIAL_FORM_DATA = {
    quantity: "",
    unitCost: "",
    reason: "",
    notes: "",
    location: "",
    adjustmentType: "add",
    locationId: defaultLocationId || "",
  };

  const [product, setProduct] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  /***** state change  *****/
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Pre-fill from alert
  useEffect(() => {
    if (initialItem) {
      setProduct(initialItem);
      if (initialItem.locationId)
        setFormData({ ...formData, locationId: initialItem.locationId });
    }
  }, [initialItem]);

  const reasons = useMemo(
    () => getReasonsForType(formData.adjustmentType),
    [formData.adjustmentType],
  );

  // Live preview of new total
  const newTotal = useMemo(() => {
    const qty = parseInt(formData.quantity, 10);
    if (!product || isNaN(qty)) return null;
    const current = product?.currentStock || 0;
    if (formData.adjustmentType === "add") return current + qty;
    if (formData.adjustmentType === "subtract") return current - qty;
    if (formData.adjustmentType === "set") return qty;
    return null;
  }, [formData.adjustmentType, formData.quantity, product]);

  const willGoNegative = newTotal !== null && newTotal < 0;

  const validate = () => {
    const errs = {};
    if (!product) errs.product = "Select a product";
    if (!formData.locationId) errs.location = "Select a location";
    if (!formData.quantity || parseInt(formData.quantity, 10) <= 0)
      errs.quantity = "Enter a positive quantity";
    if (!formData.reason) errs.reason = "Select a reason";
    if (willGoNegative) errs.quantity = "Stock cannot go below zero";
    if (formData.reason === "other" && !formData.notes.trim())
      errs.notes = "Notes required when reason is Other";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        items: [
          {
            inventoryItemId: product?.inventoryItemId ?? product?.id,
            delta: parseInt(formData.quantity, 10),
            unitCost: formData.unitCost ? parseFloat(formData.unitCost) : null,
          },
        ],
      };

      const result = await inventoryAPI.adjust(payload);

      showSuccess(`Stock updated successfully (${result.itemsUpdated} item)`);
      onSuccess?.(result);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to update stock";
      showError(msg);
      if (err.response?.data?.code === "CONCURRENT_UPDATE") {
        setErrors({
          general: "Stock was modified by another user. Please refresh.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Product selector */}
      <div className="mb-3">
        <label className="form-label">
          Product <span className="text-danger">*</span>
        </label>
        {!product ? (
          <ProductSKUSearch
            onSelect={(p) => setProduct(p)}
            locationId={formData.locationId}
            autoFocus
          />
        ) : (
          <div className="d-flex align-items-center justify-content-between border rounded p-2">
            <div>
              <div className="fw-semibold">
                {product?.productName}
                {product?.variantName && ` — ${product?.variantName}`}
              </div>
              <div className="fs--2 text-muted">
                SKU: <code>{product?.sku}</code>
              </div>
              <div className="fs--1">
                <span>
                  Available:
                  <strong
                    className={product?.available <= 0 ? "text-danger" : ""}
                  >
                    {product?.currentStock} Qty in stock
                  </strong>
                </span>{" "}
                | {" "}
                <span>Price: ₹{product?.price}/-</span>
              </div>
            </div>
            <a
              className="btn btn-sm text-danger"
              onClick={() => setProduct(null)}
            >
              <i className="ti ti-x" /> Change
            </a>
          </div>
        )}
        {errors.product && (
          <div className="text-danger small mt-1">{errors.product}</div>
        )}
      </div>

      {/* Location */}
      <div className="row">
        <div className="col-md-8 mb-3">
          <label className="form-label">
            WareHouse <span className="text-danger">*</span>
          </label>
          <LocationSelect
            value={formData.locationId}
            onChange={handleChange}
            required
            name="locationId"
          />
          {errors.wareHouse && (
            <div className="text-danger small mt-1">{errors.wareHouse}</div>
          )}
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Location (ex- A1, C3, etc.)</label>
          <input
            type="text"
            className="form-control"
            value={formData.location}
            onChange={handleChange}
            name="location"
            placeholder="A1, C3"
          />
        </div>
      </div>

      {/* Action + Quantity + Preview */}
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label className="form-label">
            Action <span className="text-danger">*</span>
          </label>
          <select
            name="adjustmentType"
            className="form-select"
            value={formData.adjustmentType}
            onChange={handleChange}
          >
            {ADJUSTMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">
            Quantity <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            min="0"
            name="quantity"
            className={`form-control ${errors.quantity ? "is-invalid" : ""}`}
            value={formData.quantity}
            onChange={handleChange}
            placeholder="0"
          />
          {errors.quantity && (
            <div className="invalid-feedback">{errors.quantity}</div>
          )}
        </div>
        <div className="col-md-4">
          <label className="form-label">New Total</label>
          <div
            className={`form-control bg-light fw-semibold ${willGoNegative ? "text-danger" : ""}`}
          >
            {newTotal !== null ? newTotal : "—"}
          </div>
        </div>
      </div>

      {/* Unit cost (only relevant for add/set with new stock) */}
      {formData.adjustmentType !== "subtract" && (
        <div className="mb-3">
          <label className="form-label">Unit Cost (optional)</label>
          <div className="input-group">
            <span className="input-group-text">₹</span>
            <input
              type="number"
              step="0.01"
              min="0"
              name="unitCost"
              className="form-control"
              value={formData.unitCost}
              onChange={handleChange}
              placeholder={
                product?.lastCost
                  ? `Last: ${Number(product?.lastCost).toFixed(2)}`
                  : "0.00"
              }
            />
          </div>
          <div className="form-text small">
            <i className="ti ti-info-circle me-1" />
            Providing unit cost updates the weighted-average cost.
          </div>
        </div>
      )}

      {/* Reason */}
      <div className="mb-3">
        <label className="form-label">
          Reason <span className="text-danger">*</span>
        </label>
        <select
          name="reason"
          className={`form-select ${errors.reason ? "is-invalid" : ""}`}
          value={formData.reason}
          onChange={handleChange}
        >
          <option value="">— Select a reason —</option>
          {reasons.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        {errors.reason && (
          <div className="invalid-feedback">{errors.reason}</div>
        )}
      </div>

      {/* Notes */}
      <div className="mb-3">
        <label className="form-label">Notes (optional)</label>
        <textarea
          className={`form-control ${errors.notes ? "is-invalid" : ""}`}
          rows="2"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder="Internal notes for audit trail..."
        />
        {errors.notes && <div className="invalid-feedback">{errors.notes}</div>}
      </div>

      {errors.general && (
        <div className="alert alert-danger">
          <i className="ti ti-alert-circle me-2" />
          {errors.general}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 pt-2 border-top">
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            loading ||
            !product ||
            !formData.locationId ||
            !formData.quantity ||
            !formData.reason ||
            willGoNegative
          }
          onClick={handleSave}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="ti ti-check me-1" /> Save Stock Update
            </>
          )}
        </button>
      </div>
    </div>
  );
}
